import * as React from 'react'
import { useRef, useState } from 'react'
import { ImageSourcePropType, View } from 'react-native'

import { H5, Stack } from '@zet/design-system'
import { Gesture, GestureDetector } from 'react-native-gesture-handler'
import { runOnJS } from 'react-native-reanimated'
import Svg, { Defs, Image, Mask, Path } from 'react-native-svg'
import { svgPathProperties } from 'svg-path-properties'

interface IPath {
  segments: string[]
  color?: string
}

interface IScratchCard {
  strokeWidth: number
  width: number
  height: number
  maskImage: React.ReactElement
  revealImagePath: ImageSourcePropType
  revealImageDimensions: { width: string; height: string }
  // revealText: React.ReactElement
  revealText: string
  onScratched: () => void
  modalScreen: boolean
}

export default function ScratchCard({
  strokeWidth,
  modalScreen,
  width,
  height,
  maskImage,
  revealImagePath,
  revealImageDimensions,
  revealText,
  onScratched,
}: IScratchCard) {
  const [paths, setPaths] = useState<IPath[]>([])
  const totalAreaScratched = useRef<number>(0)
  const [scratchedArea, setScratchedArea] = useState(0)
  const [isScratched, setIsScratched] = useState(false)

  const pan = Gesture.Pan()
    .runOnJS(true)
    .onStart((g) => {
      const newPaths = [...paths]
      newPaths[paths.length] = {
        segments: [],
        color: '#06d6a0',
      }
      newPaths[paths.length].segments.push(`M ${g.x} ${g.y}`)
      runOnJS(setPaths)(newPaths)
    })
    .onUpdate((g) => {
      const index = paths.length - 1
      const newPaths = [...paths]
      if (newPaths?.[index]?.segments) {
        newPaths[index].segments.push(`L ${g.x} ${g.y}`)
        runOnJS(setPaths)(newPaths)
      }
    })
    .onEnd(() => {
      const pathProperties = runOnJS(joinPaths)(paths)
    })
    .minDistance(1)

  const joinPaths = (paths: any) => {
    const pathProperties = new svgPathProperties(
      paths[paths.length - 1].segments.join(',')
    )
    const pathArea = pathProperties.getTotalLength() * strokeWidth
    totalAreaScratched.current += pathArea
    const cardArea = width * height
    const areaScratched = (totalAreaScratched.current / cardArea) * 100
    setScratchedArea(Math.min(areaScratched / cardArea, 0.5))
    if (areaScratched > 30) {
      setIsScratched(true)
      onScratched()
    }
  }
  return (
    <GestureDetector gesture={pan}>
      <View
        style={{
          transform: [
            modalScreen
              ? {
                  rotate: '-9deg',
                }
              : {
                  rotate: '0deg',
                },
          ],
        }}
      >
        <Svg height={height} width={width}>
          <Defs>
            <Mask
              id="Mask"
              maskUnits="userSpaceOnUse"
              x="0"
              y="0"
              width={width}
              height={height}
            >
              {paths.map((p, index) => (
                <Path
                  key={index}
                  d={p.segments.join(' ')}
                  strokeWidth={strokeWidth}
                  stroke={p.color}
                  strokeLinejoin="round"
                />
              ))}
            </Mask>
            <Mask
              id="Mask2"
              maskUnits="userSpaceOnUse"
              x="0"
              y="0"
              width={width}
              height={height}
            >
              {paths.map((p, index) => (
                <Path
                  key={index}
                  d={p.segments.join(' ')}
                  strokeWidth={strokeWidth}
                  stroke={p.color}
                  strokeLinejoin="round"
                />
              ))}
            </Mask>
          </Defs>
          {maskImage}
          <Stack>
            <Image
              x="0"
              y="0"
              width={revealImageDimensions.width}
              height={revealImageDimensions.height}
              preserveAspectRatio="xMidYMid slice"
              opacity="1"
              href={revealImagePath}
              mask={isScratched ? undefined : 'url(#Mask)'}
            />
            {isScratched && (
              <Stack className="absolute bottom-6 self-center ">
                <H5 className="text-center">{revealText}</H5>
              </Stack>
            )}
          </Stack>
        </Svg>
      </View>
    </GestureDetector>
  )
}
